import type {
  FieldSource,
  ProviderServiceCapability,
  ProviderTreatment,
} from "@louise/providers/providers.types";

/**
 * Clinic treatments & capabilities taxonomy + resolver.
 *
 * This is the data model for the merged "Treatments & capabilities" section.
 * It separates two things:
 *
 *  1. Treatment groups: the procedures a clinic actually offers, grouped by the
 *     `category` already present on each treatment in the provider data
 *     (art / preservation / diagnostic / surgical / wellness). These are real,
 *     positive signals.
 *
 *  2. Capability catalog: a curated checklist of higher-order lab, add-on and
 *     frontier capabilities. Each item resolves to a status.
 *
 * IMPORTANT, the legacy three-state model and the data gap:
 *   The provider data only carries POSITIVE signals (a treatment is listed, the
 *   on-site-lab flag is set). We can confirm a capability is OFFERED, but we can
 *   never confirm it's absent. So today every catalog item resolves to either
 *   "offered" or "unknown". "not-offered" remains available to the newer
 *   shared service resolver below when a clinic-reported or verified assertion
 *   lands. "unknown" means "not confirmed", NOT "not available".
 *
 * The shared service resolver below already accepts `serviceCapabilities`; the
 * legacy catalog remains for markets that have not migrated to the illustrated
 * service model yet.
 */

export type CapabilityStatus = "offered" | "not-offered" | "unknown";

export type TreatmentCategory =
  "art" | "preservation" | "diagnostic" | "surgical" | "wellness";

export const TREATMENT_GROUP_LABELS: Record<TreatmentCategory, string> = {
  art: "Core treatments",
  preservation: "Fertility preservation",
  diagnostic: "Diagnostics",
  surgical: "Surgical",
  wellness: "Supportive & wellness",
};

export const TREATMENT_GROUP_ORDER: TreatmentCategory[] = [
  "art",
  "preservation",
  "diagnostic",
  "surgical",
  "wellness",
];

/**
 * How established a technique is, mirroring the therapies/access evidence
 * scale. This conveys "emerging" per item via an icon, so we don't need a
 * separate frontier/emerging category.
 */
export type CapabilityEvidence = "strong" | "moderate" | "emerging";

export type CapabilityItem = {
  key: string;
  label: string;
  evidence: CapabilityEvidence;
};

export type CapabilityGroupDef = {
  key: string;
  label: string;
  items: CapabilityItem[];
};

/**
 * The curated capability catalog. This is a taxonomy of *possible* capabilities,
 * not an assertion that any given clinic has them. Per-clinic status is decided
 * by the resolver below from real signals (and is mostly "unknown" until the
 * backend supplies capability data).
 */
export const CAPABILITY_CATALOG: CapabilityGroupDef[] = [
  {
    key: "lab",
    label: "Lab & embryology",
    items: [
      {
        key: "onsite-lab",
        label: "On-site embryology lab",
        evidence: "strong",
      },
      { key: "pgt", label: "Genetic testing (PGT)", evidence: "strong" },
      { key: "blastocyst", label: "Blastocyst culture", evidence: "strong" },
      {
        key: "assisted-hatching",
        label: "Assisted hatching",
        evidence: "moderate",
      },
      { key: "time-lapse", label: "Time-lapse imaging", evidence: "moderate" },
      { key: "ai-embryo", label: "AI embryo grading", evidence: "emerging" },
    ],
  },
  {
    key: "other",
    label: "Other",
    items: [
      {
        key: "onco",
        label: "Onco-fertility preservation",
        evidence: "strong",
      },
      {
        key: "era",
        label: "Endometrial receptivity (ERA)",
        evidence: "moderate",
      },
      { key: "embryoglue", label: "EmbryoGlue", evidence: "moderate" },
      {
        key: "ovarian-tissue",
        label: "Ovarian tissue cryopreservation",
        evidence: "emerging",
      },
      { key: "ivm", label: "IVM (in-vitro maturation)", evidence: "emerging" },
      {
        key: "immunology",
        label: "Reproductive immunology",
        evidence: "emerging",
      },
      {
        key: "prp",
        label: "PRP (ovarian / endometrial)",
        evidence: "emerging",
      },
      { key: "peptides", label: "Peptide therapies", evidence: "emerging" },
      { key: "longevity", label: "Longevity protocols", evidence: "emerging" },
    ],
  },
];

export type ResolvedTreatmentGroup = {
  category: TreatmentCategory;
  label: string;
  treatments: string[];
};

export type ResolvedCapability = CapabilityItem & { status: CapabilityStatus };

export type ResolvedCapabilityGroup = {
  key: string;
  label: string;
  items: ResolvedCapability[];
  offeredCount: number;
};

export type ClinicCapabilities = {
  treatmentGroups: ResolvedTreatmentGroup[];
  capabilityGroups: ResolvedCapabilityGroup[];
};

type ResolveInput = {
  treatments: { name: string; category?: string }[];
  hasOnSiteLab?: boolean;
};

export function resolveClinicCapabilities(
  input: ResolveInput,
): ClinicCapabilities {
  const { treatments, hasOnSiteLab } = input;

  // 1. Group the clinic's real treatments by category (dedupe, preserve order).
  const byCategory = new Map<TreatmentCategory, string[]>();
  for (const t of treatments) {
    const cat = (t.category as TreatmentCategory) || "art";
    if (!TREATMENT_GROUP_LABELS[cat]) continue;
    const arr = byCategory.get(cat) ?? [];
    if (!arr.includes(t.name)) arr.push(t.name);
    byCategory.set(cat, arr);
  }
  const treatmentGroups: ResolvedTreatmentGroup[] =
    TREATMENT_GROUP_ORDER.filter(
      (cat) => (byCategory.get(cat) ?? []).length > 0,
    ).map((cat) => ({
      category: cat,
      label: TREATMENT_GROUP_LABELS[cat],
      treatments: byCategory.get(cat) ?? [],
    }));

  // 2. Resolve curated capabilities from the only positive signals we have.
  const names = treatments.map((t) => t.name.toLowerCase());
  const hasAny = (...needles: string[]) =>
    names.some((n) => needles.some((needle) => n.includes(needle)));

  const offered = new Set<string>();
  if (hasOnSiteLab) offered.add("onsite-lab");
  if (hasAny("pgt", "pgs", "pgd", "genetic")) offered.add("pgt");
  // Everything else stays "unknown" until a capability data source exists.

  const capabilityGroups: ResolvedCapabilityGroup[] = CAPABILITY_CATALOG.map(
    (group) => {
      const items: ResolvedCapability[] = group.items.map((item) => ({
        ...item,
        status: offered.has(item.key) ? "offered" : "unknown",
      }));
      return {
        key: group.key,
        label: group.label,
        items,
        offeredCount: items.filter((i) => i.status === "offered").length,
      };
    },
  );

  return { treatmentGroups, capabilityGroups };
}

// ---------------------------------------------------------------------------
// Shared illustrated clinic services
// ---------------------------------------------------------------------------

/**
 * The U.S. and French detail pages use a service-first model instead of the
 * legacy treatment/capability split above. The catalog is intentionally small: it
 * names the services patients actually compare and only adds a service when
 * provider data or an explicit capability assertion gives us a reason to show
 * it. Unknown is never treated as not offered.
 */
export type ClinicServiceGroupKey =
  | "treatment-options"
  | "fertility-preservation"
  | "testing-lab"
  | "donor-lgbtq-surrogacy-support";

export type ClinicServiceKey =
  | "ivf"
  | "iui"
  | "mini-ivf"
  | "ovulation-induction"
  | "egg-freezing"
  | "embryo-freezing"
  | "sperm-freezing"
  | "fertility-preservation"
  | "onsite-lab"
  | "fertility-testing"
  | "pgt-family"
  | "donor-programs"
  | "gestational-carrier"
  | "lgbtq-care"
  | "monitoring";

export type PgtSubtypeKey = "pgt-a" | "pgt-m" | "pgt-sr" | "pgt-p";

export type ResolvedClinicService = {
  key: ClinicServiceKey;
  label: string;
  status: CapabilityStatus;
  source?: FieldSource;
  verifiedAt?: string;
};

export type ResolvedPgtSubtype = {
  key: PgtSubtypeKey;
  label: string;
  status: CapabilityStatus;
  source?: FieldSource;
  verifiedAt?: string;
};

export type ResolvedClinicServiceGroup = {
  key: ClinicServiceGroupKey;
  label: string;
  items: ResolvedClinicService[];
};

export type ClinicServices = {
  groups: ResolvedClinicServiceGroup[];
  pgtSubtypes: ResolvedPgtSubtype[];
};

type ClinicServiceDefinition = {
  key: ClinicServiceKey;
  label: string;
  group: ClinicServiceGroupKey;
  matches?: (name: string) => boolean;
  includeWhen?: (input: ResolveClinicServicesInput) => boolean;
  inferOfferedWhen?: (input: ResolveClinicServicesInput) => boolean;
};

export type ResolveClinicServicesInput = {
  treatments: ProviderTreatment[];
  patientGroups?: string[];
  hasOnSiteLab?: boolean;
  capabilityOverrides?: ProviderServiceCapability[];
  defaultSource?: FieldSource;
};

const PGT_SUBTYPES: { key: PgtSubtypeKey; label: string }[] = [
  { key: "pgt-a", label: "PGT-A" },
  { key: "pgt-m", label: "PGT-M" },
  { key: "pgt-sr", label: "PGT-SR" },
  { key: "pgt-p", label: "PGT-P" },
];

const hasName =
  (...needles: string[]) =>
  (name: string) =>
    needles.some((needle) => name.includes(needle));

const US_SERVICE_DEFINITIONS: ClinicServiceDefinition[] = [
  {
    key: "ivf",
    label: "IVF & ICSI",
    group: "treatment-options",
    matches: hasName("ivf", "icsi"),
  },
  {
    key: "iui",
    label: "IUI",
    group: "treatment-options",
    matches: hasName("iui"),
  },
  {
    key: "ovulation-induction",
    label: "Ovulation induction",
    group: "treatment-options",
    matches: hasName("ovulation induction"),
  },
  {
    key: "mini-ivf",
    label: "Mini-IVF",
    group: "treatment-options",
    matches: hasName("miniivf", "mini ivf"),
  },
  {
    key: "egg-freezing",
    label: "Egg freezing",
    group: "fertility-preservation",
    matches: hasName("egg freezing", "oocyte freezing"),
  },
  {
    key: "embryo-freezing",
    label: "Embryo freezing",
    group: "fertility-preservation",
    matches: hasName("embryo freezing", "embryo cryo"),
  },
  {
    key: "sperm-freezing",
    label: "Sperm freezing",
    group: "fertility-preservation",
    matches: hasName("sperm freezing", "sperm cryo"),
  },
  {
    key: "fertility-preservation",
    label: "Fertility preservation",
    group: "fertility-preservation",
    matches: hasName("fertility preservation"),
  },
  {
    key: "onsite-lab",
    label: "On-site embryology lab",
    group: "testing-lab",
    includeWhen: ({ hasOnSiteLab, capabilityOverrides }) =>
      hasOnSiteLab === true ||
      capabilityOverrides?.some((item) => item.key === "onsite-lab") === true,
    inferOfferedWhen: ({ hasOnSiteLab }) => hasOnSiteLab === true,
  },
  {
    key: "fertility-testing",
    label: "Fertility testing",
    group: "testing-lab",
    matches: hasName("fertility testing", "fertility assessment"),
  },
  {
    key: "pgt-family",
    label: "PGT (preimplantation genetic testing)",
    group: "testing-lab",
    includeWhen: ({ capabilityOverrides }) =>
      capabilityOverrides?.some(
        (item) => item.key === "pgt-family" || item.key.startsWith("pgt-"),
      ) === true,
    matches: hasName("pgt", "pgs", "pgd", "genetic"),
  },
  {
    key: "donor-programs",
    label: "Donor programs",
    group: "donor-lgbtq-surrogacy-support",
    matches: hasName("donor"),
  },
  {
    key: "gestational-carrier",
    label: "Gestational carrier support",
    group: "donor-lgbtq-surrogacy-support",
    matches: hasName("surrogacy", "gestational carrier"),
  },
  {
    key: "lgbtq-care",
    label: "LGBTQ+ care",
    group: "donor-lgbtq-surrogacy-support",
    includeWhen: ({ patientGroups, capabilityOverrides }) =>
      patientGroups?.some((group) =>
        /lgbtq|same sex|gay|lesbian|queer/i.test(group),
      ) === true ||
      capabilityOverrides?.some((item) => item.key === "lgbtq-care") === true,
    inferOfferedWhen: ({ patientGroups }) =>
      patientGroups?.some((group) =>
        /lgbtq|same sex|gay|lesbian|queer/i.test(group),
      ) === true,
  },
  {
    key: "monitoring",
    label: "Monitoring appointments",
    group: "donor-lgbtq-surrogacy-support",
    includeWhen: ({ capabilityOverrides }) =>
      capabilityOverrides?.some((item) => item.key === "monitoring") === true,
  },
];

const statusFromTreatment = (
  treatment: ProviderTreatment | undefined,
): CapabilityStatus => treatment?.status ?? (treatment ? "offered" : "unknown");

/** Resolve normalized services without inferring absence from missing data. */
export function resolveClinicServices(
  input: ResolveClinicServicesInput,
): ClinicServices {
  const treatments = input.treatments ?? [];
  const overrides = new Map(
    (input.capabilityOverrides ?? []).map((item) => [item.key, item]),
  );
  const pgtTreatment = treatments.find((treatment) =>
    /pgt|pgs|pgd|genetic/i.test(treatment.name),
  );

  const resolved = US_SERVICE_DEFINITIONS.flatMap((definition) => {
    const treatment = definition.matches
      ? treatments.find((candidate) =>
          definition.matches!(candidate.name.toLowerCase()),
        )
      : undefined;
    const override = overrides.get(definition.key);
    const included =
      override != null ||
      treatment != null ||
      definition.includeWhen?.(input) === true;
    if (!included) return [];

    const source = override
      ? override._source
      : (treatment?._source ?? input.defaultSource);
    return [
      {
        key: definition.key,
        label: definition.label,
        status:
          override?.status ??
          (definition.inferOfferedWhen?.(input)
            ? "offered"
            : statusFromTreatment(treatment)),
        source,
        verifiedAt: override?.verifiedAt ?? source?.fetchedAt,
      } satisfies ResolvedClinicService,
    ];
  });

  const pgtSubtypes = PGT_SUBTYPES.map(({ key, label }) => {
    const override = overrides.get(key);
    const source = override
      ? override._source
      : (pgtTreatment?._source ?? input.defaultSource);
    return {
      key,
      label,
      status: override?.status ?? "unknown",
      source,
      verifiedAt: override?.verifiedAt ?? source?.fetchedAt,
    } satisfies ResolvedPgtSubtype;
  });

  const groupLabels: Record<ClinicServiceGroupKey, string> = {
    "treatment-options": "Treatment options",
    "fertility-preservation": "Fertility preservation",
    "testing-lab": "Testing & lab services",
    "donor-lgbtq-surrogacy-support":
      "Donor, LGBTQ+, surrogacy & support services",
  };
  const groupOrder: ClinicServiceGroupKey[] = [
    "treatment-options",
    "fertility-preservation",
    "testing-lab",
    "donor-lgbtq-surrogacy-support",
  ];

  return {
    groups: groupOrder
      .map((key) => ({
        key,
        label: groupLabels[key],
        items: resolved.filter((item) =>
          item.key === "pgt-family"
            ? key === "testing-lab"
            : US_SERVICE_DEFINITIONS.find(
                (definition) => definition.key === item.key,
              )?.group === key,
        ),
      }))
      .filter((group) => group.items.length > 0),
    pgtSubtypes,
  };
}
